import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    FiCheck,
    FiChevronDown,
    FiInfo,
    FiSave,
    FiPlus,
    FiEdit2,
    FiX,
} from "react-icons/fi";
import Header from "units/dashboard/components/Header";
import { SidebarProvider } from "units/dashboard/context/SidebarProvider";
import AdminSidebar from "../components/AdminSidebar";
import { ProgressBar } from "react-loader-spinner";
import {
    createTemplate,
    getCriteria,
    updateStageNames,
} from "./services/adminSellFunnel";

// ============================
// 6 Stages Definition - با نام‌های قابل تغییر
// ============================
const DEFAULT_STAGE_LABELS = [
    { value: 1, label: "مراجعه‌کننده" },
    { value: 2, label: "علاقه‌مند به محصول" },
    { value: 3, label: "علاقه‌مند به خرید" },
    { value: 4, label: "مشتری" },
    { value: 5, label: "مشتری وفادار" },
    { value: 6, label: "مشتری ناراضی" },
];

const CreateReadyFunnel = () => {
    const accessToken = useSelector((state) => state.auth?.accessToken);

    // ============================
    // Form States
    // ============================
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");

    // ============================
    // Stage Names States (قابل ویرایش)
    // ============================
    const [stageLabels, setStageLabels] = useState(DEFAULT_STAGE_LABELS);
    const [editingStage, setEditingStage] = useState(null);
    const [editStageName, setEditStageName] = useState("");

    // ============================
    // Criteria Data from API
    // ============================
    const [criteriaData, setCriteriaData] = useState([]);
    const [criteriaLoading, setCriteriaLoading] = useState(true);
    const [criteriaError, setCriteriaError] = useState("");

    // ============================
    // Selected Criteria per Stage
    // ============================
    const [selectedCriteria, setSelectedCriteria] = useState({});
    const [openDetails, setOpenDetails] = useState({});

    // ============================
    // Saving States
    // ============================
    const [savingInfo, setSavingInfo] = useState(false);
    const [savingStage, setSavingStage] = useState(null);
    const [savingStageNames, setSavingStageNames] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // ============================
    // Load Criteria from API
    // ============================
    const loadCriteria = async () => {
        if (!accessToken) {
            setCriteriaLoading(false);
            return;
        }

        try {
            setCriteriaLoading(true);
            setCriteriaError("");
            const response = await getCriteria(accessToken);
            setCriteriaData(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("GET CRITERIA ERROR:", err);
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                "دریافت لیست معیارها با خطا مواجه شد.";
            setCriteriaError(backendMessage);
        } finally {
            setCriteriaLoading(false);
        }
    };

    useEffect(() => {
        loadCriteria();
    }, [accessToken]);

    // ============================
    // Get Criteria by Stage Order
    // ============================
    const getCriteriaByStage = (stageOrder) => {
        return criteriaData.filter(
            (item) => Number(item.stageOrder) === stageOrder && 
            item.isActive === true && 
            item.isVisible === true
        );
    };

    // ============================
    // Get Selected Criterion
    // ============================
    const getSelectedCriterion = (stageOrder) => {
        const criteriaList = getCriteriaByStage(stageOrder);
        const selectedId = selectedCriteria[stageOrder];
        return criteriaList.find((c) => c.id === selectedId) || null;
    };

    // ============================
    // Handle Criteria Change
    // ============================
    const handleCriteriaChange = (stageOrder, criterionId) => {
        setSelectedCriteria((prev) => ({
            ...prev,
            [stageOrder]: criterionId,
        }));
    };

    // ============================
    // Toggle Details
    // ============================
    const toggleDetails = (stageOrder) => {
        setOpenDetails((prev) => ({
            ...prev,
            [stageOrder]: !prev[stageOrder],
        }));
    };

    // ============================
    // Stage Name Editing Functions
    // ============================
    const startEditingStage = (stage) => {
        setEditingStage(stage.value);
        setEditStageName(stage.label);
    };

    const cancelEditingStage = () => {
        setEditingStage(null);
        setEditStageName("");
    };

    const saveStageName = (stageValue) => {
        if (!editStageName.trim()) {
            setError("نام مرحله نمی‌تواند خالی باشد.");
            return;
        }

        setStageLabels((prev) =>
            prev.map((s) =>
                s.value === stageValue
                    ? { ...s, label: editStageName.trim() }
                    : s
            )
        );
        setEditingStage(null);
        setEditStageName("");
        setSuccessMessage(`نام مرحله ${stageValue} با موفقیت تغییر کرد.`);
        setTimeout(() => setSuccessMessage(""), 3000);
    };

    // ============================
    // Save All Stage Names to Backend
    // ============================
    const handleSaveStageNames = async () => {
        if (!accessToken) {
            setError("توکن احراز هویت یافت نشد.");
            return;
        }

        // ابتدا باید template ایجاد شده باشد
        if (!name.trim()) {
            setError("ابتدا نام قیف را وارد کرده و قیف را ایجاد کنید.");
            return;
        }

        setError("");
        setSuccessMessage("");
        setSavingStageNames(true);

        try {
            // ابتدا template را ایجاد می‌کنیم (اگر هنوز ایجاد نشده)
            const stages = stageLabels.map((stage) => ({
                stageOrder: stage.value,
                stageName: stage.label,
                criterionId: selectedCriteria[stage.value] || null,
            }));

            // بررسی اینکه همه مراحل پر شده باشند
            const emptyStages = stages.filter(s => !s.criterionId);
            if (emptyStages.length > 0) {
                setError(`لطفاً برای همه مراحل معیار انتخاب کنید. (مراحل ${emptyStages.map(s => s.stageOrder).join(', ')} خالی هستند)`);
                setSavingStageNames(false);
                return;
            }

            // ابتدا template را ایجاد می‌کنیم
            const templateData = {
                name: name.trim(),
                businessCategory: category.trim() || "general",
                stages: stages,
            };

            const createResponse = await createTemplate(templateData, accessToken);
            const templateId = createResponse?.id || createResponse?.data?.id;

            if (!templateId) {
                setError("ایجاد قیف با خطا مواجه شد. شناسه قیف دریافت نشد.");
                setSavingStageNames(false);
                return;
            }

            // حالا نام مراحل را به‌روزرسانی می‌کنیم
            const stageNamesData = stageLabels.map((stage) => ({
                stageOrder: stage.value,
                stageName: stage.label,
            }));

            await updateStageNames(templateId, stageNamesData, accessToken);
            
            setSuccessMessage("قیف آماده با نام‌های مراحل جدید با موفقیت ایجاد شد.");
            setName("");
            setCategory("");
            setSelectedCriteria({});
            
        } catch (err) {
            console.error("SAVE STAGE NAMES ERROR:", err);
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                "ذخیره نام مراحل با خطا مواجه شد.";
            setError(backendMessage);
        } finally {
            setSavingStageNames(false);
        }
    };

    // ============================
    // Save Template Info
    // ============================
    const handleSaveInfo = async () => {
        if (!name.trim()) {
            setError("نام قیف را وارد کنید.");
            return;
        }

        setError("");
        setSuccessMessage("");
        setSavingInfo(true);

        try {
            const stages = stageLabels.map((stage) => ({
                stageOrder: stage.value,
                stageName: stage.label,
                criterionId: selectedCriteria[stage.value] || null,
            }));

            const emptyStages = stages.filter(s => !s.criterionId);
            if (emptyStages.length > 0) {
                setError(`لطفاً برای همه مراحل معیار انتخاب کنید. (مراحل ${emptyStages.map(s => s.stageOrder).join(', ')} خالی هستند)`);
                setSavingInfo(false);
                return;
            }

            const data = {
                name: name.trim(),
                businessCategory: category.trim() || "general",
                stages: stages,
            };

            await createTemplate(data, accessToken);
            setSuccessMessage("قیف آماده با موفقیت ایجاد شد.");
            
            setName("");
            setCategory("");
            setSelectedCriteria({});
            
        } catch (err) {
            console.error("CREATE TEMPLATE ERROR:", err);
            const backendMessage =
                err?.response?.data?.message ||
                err?.response?.data?.detail ||
                "ایجاد قیف با خطا مواجه شد.";
            setError(backendMessage);
        } finally {
            setSavingInfo(false);
        }
    };

    // ============================
    // Save Single Stage
    // ============================
    const handleSaveStage = (stageOrder) => {
        setError("");
        setSuccessMessage("");
        setSavingStage(stageOrder);

        const selected = getSelectedCriterion(stageOrder);
        
        if (!selected) {
            setError(`لطفاً یک معیار برای مرحله ${stageOrder} انتخاب کنید.`);
            setSavingStage(null);
            return;
        }

        setTimeout(() => {
            setSavingStage(null);
            setSuccessMessage(`مرحله ${stageOrder} با موفقیت ذخیره شد.`);
            setTimeout(() => setSuccessMessage(""), 3000);
        }, 500);
    };

    // ============================
    // Get Type Badge
    // ============================
    const getTypeBadge = (type) => {
        if (type === "AI") {
            return (
                <span className="inline-flex rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-medium text-teal-700">
                    AI
                </span>
            );
        }
        return (
            <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700">
                SYSTEM_RULE
            </span>
        );
    };

    // ============================
    // Render
    // ============================
    return (
        <SidebarProvider>
            <nav className="fixed left-0 right-0 top-0 z-40 flex items-center border-b border-teal-100 bg-white print:hidden dark:border-zinc-700 dark:bg-zinc-800">
                <Header />
            </nav>

            <div className="vertical-menu rtl:right-0 ltr:left-0 fixed bottom-0 top-16 z-30 h-[calc(100vh-4rem)] border-r border-teal-100 bg-white print:hidden dark:border-neutral-700 dark:bg-zinc-800">
                <AdminSidebar />
            </div>

            <main dir="rtl" className="min-h-screen pt-20 overflow-x-hidden bg-[#F8FAFA] transition-all duration-300 lg:mr-[250px]">
                {loading ? (
                    <div className="flex min-h-[calc(100vh-5rem)] items-center justify-center">
                        <ProgressBar visible={true} height="80" width="80" color="#0F766E" />
                    </div>
                ) : (
                    <div className="mx-auto w-full max-w-[1100px] px-4 pb-8 sm:px-6">
                        {/* Page Header */}
                        <div className="mb-7">
                            <div className="mb-2 flex items-center gap-2">
                                <div className="h-8 w-1 rounded-full bg-[#D4A017]" />
                                <h1 className="text-2xl font-bold text-[#173B3A]">
                                    ایجاد قیف آماده
                                </h1>
                            </div>
                            <p className="max-w-3xl text-sm leading-7 text-gray-500">
                                یک قیف آماده با ۶ مرحله تعریف کنید تا مدیران کسب‌وکار
                                بتوانند آن را به‌عنوان قالب اولیه برای قیف
                                فروش خود انتخاب کنند.
                            </p>
                        </div>

                        {/* Error/Success Messages */}
                        {error && (
                            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                {error}
                            </div>
                        )}
                        {successMessage && (
                            <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-700">
                                {successMessage}
                            </div>
                        )}

                        {/* General Information */}
                        <div className="mb-6 overflow-hidden rounded-2xl border border-teal-100 bg-white shadow-sm">
                            <div className="border-b border-teal-50 bg-gradient-to-l from-teal-50/70 to-white px-5 py-4 sm:px-6">
                                <h2 className="text-lg font-bold text-[#173B3A]">
                                    اطلاعات کلی
                                </h2>
                                <p className="mt-1 text-xs text-gray-500">
                                    مشخصات اصلی قیف آماده را وارد کنید.
                                </p>
                            </div>

                            <div className="p-5 sm:p-6">
                                <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-[1fr_1fr_auto]">
                                    <div>
                                        <label className="mb-2 block text-sm font-semibold text-gray-700">
                                            نام
                                            <span className="mr-1 text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="مثلاً قیف فروشگاهی"
                                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-2 block text-sm font-semibold text-gray-700">
                                            دسته‌بندی نمایشی
                                        </label>
                                        <input
                                            type="text"
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            placeholder="مثلاً e-commerce"
                                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleSaveInfo}
                                        disabled={savingInfo}
                                        className="inline-flex h-[42px] items-center justify-center gap-2 rounded-lg bg-teal-700 px-5 text-sm font-medium text-white shadow-sm transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <FiSave />
                                        {savingInfo ? "در حال ایجاد..." : "ایجاد قیف"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Criteria Selection */}
                        <div className="overflow-hidden rounded-2xl border border-teal-100 bg-white shadow-sm">
                            <div className="border-b border-teal-50 bg-gradient-to-l from-teal-50/70 to-white px-5 py-4 sm:px-6">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <h2 className="text-lg font-bold text-[#173B3A]">
                                            معیار هر دسته
                                        </h2>
                                        <p className="mt-1 text-xs text-gray-500">
                                            برای هر مرحله یک معیار از معیارهای
                                            همان دسته انتخاب کنید.
                                        </p>
                                    </div>
                                    <div className="hidden rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 sm:block">
                                        ۶ مرحله
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 sm:p-6">
                                {criteriaLoading ? (
                                    <div className="flex items-center justify-center py-10">
                                        <ProgressBar visible={true} height="60" width="60" color="#0F766E" />
                                    </div>
                                ) : criteriaError ? (
                                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                        {criteriaError}
                                    </div>
                                ) : (
                                    <>
                                        {stageLabels.map((stage) => {
                                            const availableCriteria = getCriteriaByStage(stage.value);
                                            const selected = getSelectedCriterion(stage.value);
                                            const isEditing = editingStage === stage.value;

                                            return (
                                                <div
                                                    key={stage.value}
                                                    className="mb-4 w-full rounded-xl border border-gray-200 bg-white p-4 transition last:mb-0 hover:border-teal-200 hover:shadow-sm"
                                                >
                                                    {/* Stage Header با قابلیت ویرایش نام */}
                                                    <div className="mb-4 flex items-center gap-3">
                                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-50 text-sm font-bold text-teal-700 ring-1 ring-teal-100">
                                                            {stage.value}
                                                        </div>
                                                        
                                                        <div className="min-w-0 flex-1">
                                                            {isEditing ? (
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="text"
                                                                        value={editStageName}
                                                                        onChange={(e) => setEditStageName(e.target.value)}
                                                                        className="flex-1 rounded-lg border border-teal-300 bg-white px-3 py-1.5 text-sm text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                                                                        autoFocus
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter") {
                                                                                saveStageName(stage.value);
                                                                            }
                                                                            if (e.key === "Escape") {
                                                                                cancelEditingStage();
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        onClick={() => saveStageName(stage.value)}
                                                                        className="rounded-lg bg-teal-600 p-1.5 text-white transition hover:bg-teal-700"
                                                                    >
                                                                        <FiCheck size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={cancelEditingStage}
                                                                        className="rounded-lg bg-gray-200 p-1.5 text-gray-600 transition hover:bg-gray-300"
                                                                    >
                                                                        <FiX size={16} />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-2">
                                                                    <div className="truncate text-sm font-bold text-[#173B3A]">
                                                                        {stage.label}
                                                                    </div>
                                                                    <button
                                                                        onClick={() => startEditingStage(stage)}
                                                                        className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                                                                        title="ویرایش نام مرحله"
                                                                    >
                                                                        <FiEdit2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                            <div className="mt-0.5 text-xs text-gray-400">
                                                                {availableCriteria.length} معیار موجود
                                                            </div>
                                                        </div>
                                                        
                                                        {selected && (
                                                            <FiCheck className="mr-auto text-teal-600" size={18} />
                                                        )}
                                                    </div>

                                                    {/* Select + Buttons */}
                                                    <div className="flex mobile:flex-col md:flex-row gap-3 md:items-end w-full">
                                                        <div className="min-w-0 flex-1">
                                                            <label className="mb-2 block text-xs font-semibold text-gray-600">
                                                                معیار این دسته
                                                            </label>

                                                            <div className="relative w-full">
                                                                <select
                                                                    value={selectedCriteria[stage.value] || ""}
                                                                    onChange={(e) =>
                                                                        handleCriteriaChange(
                                                                            stage.value,
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    className="h-[42px] w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 pl-10 text-sm text-gray-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/10"
                                                                >
                                                                    <option value="">انتخاب معیار...</option>
                                                                    {availableCriteria.map((criterion) => (
                                                                        <option key={criterion.id} value={criterion.id}>
                                                                            {criterion.title}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <FiChevronDown
                                                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                                                    size={16}
                                                                />
                                                            </div>
                                                            {availableCriteria.length === 0 && (
                                                                <p className="mt-1 text-xs text-amber-600">
                                                                    ⚠️ هیچ معیار فعالی برای این دسته وجود ندارد.
                                                                </p>
                                                            )}
                                                        </div>

                                                        {/* Details */}
                                                        {selected && (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleDetails(stage.value)}
                                                                className="inline-flex h-[42px] w-full shrink-0 items-center justify-center gap-2 rounded-lg px-3 text-xs font-medium text-teal-700 transition hover:bg-teal-50 sm:w-auto lg:w-[145px]"
                                                            >
                                                                <FiInfo size={15} />
                                                                <span>جزئیات معیار</span>
                                                                <FiChevronDown
                                                                    size={15}
                                                                    className={`transition-transform ${openDetails[stage.value] ? "rotate-180" : ""}`}
                                                                />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Details Panel */}
                                                    {openDetails[stage.value] && selected && (
                                                        <div className="mt-4 w-full rounded-lg border border-teal-100 bg-[#F8FAFA] p-4">
                                                            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                                                                <span className="text-xs font-bold text-[#173B3A]">
                                                                    جزئیات معیار
                                                                </span>
                                                                {getTypeBadge(selected.type)}
                                                            </div>

                                                            <div className="mb-3 grid grid-cols-1 gap-1 text-xs sm:grid-cols-[80px_1fr] sm:gap-2">
                                                                <span className="font-bold text-gray-600">نوع:</span>
                                                                <span className="text-gray-600">
                                                                    {selected.type === "AI"
                                                                        ? "هوش مصنوعی (AI)"
                                                                        : "قانون سیستمی (SYSTEM_RULE)"}
                                                                </span>
                                                            </div>

                                                            {selected.type === "SYSTEM_RULE" && selected.ruleConfig && (
                                                                <>
                                                                    <div className="mb-3 grid grid-cols-1 gap-1 text-xs sm:grid-cols-[80px_1fr] sm:gap-2">
                                                                        <span className="font-bold text-gray-600">فیلد:</span>
                                                                        <span className="break-words text-gray-600">
                                                                            {selected.ruleConfig.field}
                                                                        </span>
                                                                    </div>
                                                                    <div className="mb-3 grid grid-cols-1 gap-1 text-xs sm:grid-cols-[80px_1fr] sm:gap-2">
                                                                        <span className="font-bold text-gray-600">شرط:</span>
                                                                        <span className="break-words text-gray-600">
                                                                            {selected.ruleConfig.operator} {selected.ruleConfig.value}
                                                                        </span>
                                                                    </div>
                                                                </>
                                                            )}

                                                            {selected.type === "AI" && selected.prompt && (
                                                                <div className="mb-3 grid grid-cols-1 gap-1 text-xs sm:grid-cols-[80px_1fr] sm:gap-2">
                                                                    <span className="font-bold text-gray-600">Prompt:</span>
                                                                    <span className="break-words leading-6 text-gray-600">
                                                                        {selected.prompt}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            <div className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-[80px_1fr] sm:gap-2">
                                                                <span className="font-bold text-gray-600">وضعیت:</span>
                                                                <span className="font-medium text-teal-700">
                                                                    {selected.isActive && selected.isVisible
                                                                        ? "فعال / نمایان"
                                                                        : selected.isActive
                                                                        ? "غیرنمایان"
                                                                        : "غیرفعال"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}

                                {/* Notice */}
                                <div className="mt-5 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-7 text-amber-800">
                                    <div className="flex gap-2">
                                        <FiInfo className="mt-1 shrink-0 text-amber-600" />
                                        <span>
                                            هر دراپ‌داون فقط معیارهای همان دسته
                                            را نشان می‌دهد؛ چون هر معیار دقیقاً
                                            برای یک دسته تعریف شده است. دکمه
                                            «جزئیات معیار» فقط نمایشی است و ویرایش
                                            Prompt یا قانون از اینجا انجام نمی‌شود.
                                            برای ویرایش معیار باید از صفحه
                                            «مدیریت معیارها» استفاده شود.
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        <FiEdit2 className="mt-1 shrink-0 text-amber-600" />
                                        <span>
                                            برای تغییر نام هر مرحله، روی آیکون مداد 
                                            کنار نام مرحله کلیک کنید.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </SidebarProvider>
    );
};

export default CreateReadyFunnel;